import React, { Dispatch, SetStateAction } from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { Group } from '@lightspeed/flame/Group';
import { IconInfo } from '@lightspeed/flame/Icon/Info';
import { IconWarning } from '@lightspeed/flame/Icon/Warning';
import { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text, TextLink } from '@lightspeed/flame/Text';
import axios from 'axios';

import useLocalizedLink from 'root/apps/hooks/useLocalizedLink';

import { ADD_PAYMENT_DEPOSIT_MODAL_ERRORS } from './AddPaymentDepositModal';
import { StyledBox, StyledCard, StyledRequestDepositModal } from './AddPaymentDepositModal.style';

export const HELP_CENTRE_ARTICLE_LINK =
  'https://retail-support.lightspeedhq.com/hc/{{locale}}/articles/25958383346971';

interface PaylinkModal {
  isModalOpen: boolean;
  isLoading: boolean;
  setModalView: Dispatch<SetStateAction<string>>;
  setPreviousModalView: Dispatch<SetStateAction<string>>;
  setPaylinkCardOption: Dispatch<SetStateAction<string>>;
  paylinkCardOption: string;
  handleSubmit: () => Promise<void>;
  isError: boolean;
}

const RequestDepositPaymentlink: React.FC<PaylinkModal> = ({
  isModalOpen,
  isLoading,
  setModalView,
  setPreviousModalView,
  setPaylinkCardOption,
  paylinkCardOption,
  handleSubmit,
  isError,
}) => {
  const [selectedOption, setSelectedOption] = React.useState(paylinkCardOption);
  const [isCustomTemplateUpdated, setIsCustomTemplateUpdated] = React.useState<boolean>(true);

  React.useEffect(() => {
    const checkIsSalesReceiptTemplateUpdated = async () => {
      try {
        const response = await axios.get(
          '/admin/display-template/is-template-support?template=SaleReceipt&feature=remoteDeposit'
        );

        if (response.data?.data?.isSupported) {
          setIsCustomTemplateUpdated(true);
          return true;
        }

        setIsCustomTemplateUpdated(false);
        return false;
      } catch (err) {
        console.warn(err);
      }
      return false;
    };

    checkIsSalesReceiptTemplateUpdated();
  }, []);

  const handleHoverCardSelect = (option: string) => {
    if (option === 'by-email') {
      setSelectedOption('by-email');
    } else {
      setSelectedOption('paylink-only');
    }
  };

  const handleModalClose = () => {
    setModalView('cancel-deposit');
    setPreviousModalView('request-deposit-paylink');
    setPaylinkCardOption(selectedOption);
  };

  const HELP_CENTRE_LOCALIZED_LINK = useLocalizedLink(HELP_CENTRE_ARTICLE_LINK);

  return (
    <StyledRequestDepositModal isOpen={isModalOpen} shouldCloseOnOverlayClick={false}>
      <ModalHeader
        onCloseClicked={() => handleModalClose()}
        showCloseButton={true}
        data-test="request-paylink-modal-header"
      >
        {isLoading ? (
          <Bone height={5} data-test="request-paylink-bones-loading" />
        ) : (
          <Text>Request deposit via payment link </Text>
        )}
      </ModalHeader>
      <ModalBody>
        {isError && (
          <Alert
            mb={3}
            type="danger"
            noCloseBtn
            title={ADD_PAYMENT_DEPOSIT_MODAL_ERRORS.ERROR_ADDING_IN_PERSON_DEPOSIT.title}
          >
            {ADD_PAYMENT_DEPOSIT_MODAL_ERRORS.ERROR_ADDING_IN_PERSON_DEPOSIT.message}
          </Alert>
        )}
        {isLoading ? (
          <>
            <StyledBox>
              <Bone height={132} width="49%" />
              <Bone height={132} width="49%" />
            </StyledBox>
            <Bone height={5} style={{ marginTop: '10px' }} />
          </>
        ) : (
          <>
            <StyledBox>
              <StyledCard
                data-test="request-paylink-modal-hovercard-send-by-email"
                showBadge={false}
                selected={selectedOption === 'by-email'}
                onClick={() => handleHoverCardSelect('by-email')}
                title="Generate payment link &amp; send by email"
                content="A receipt including a payment link will be generated and emailed to your customer once you finish the sale."
              />
              <StyledCard
                data-test="request-paylink-modal-hovercard-paylink-only"
                showBadge={false}
                selected={selectedOption === 'paylink-only'}
                onClick={() => handleHoverCardSelect('paylink-only')}
                title="Generate payment link only"
                content="A receipt including a payment link will be generated once you finish the sale."
              />
            </StyledBox>
            {isCustomTemplateUpdated ? (
              <Alert
                data-test="request-paylink-alert-info"
                type="info"
                icon={<IconInfo baseColor="none" detailsColor="#000" />}
                noCloseBtn
                css={{
                  border: '1px solid #D6DFFD !important',
                  fontSize: '14px',
                  boxShadow: 'none',
                }}
              >
                You can always copy and share the payment link using your preferred communication
                method.
              </Alert>
            ) : (
              <Alert
                data-test="request-payment-link-alert-sales-template-not-updated"
                type="warning"
                icon={<IconWarning color="#ea891e" />}
                noCloseBtn
                css={{
                  border: '1px solid #FFEDD4 !important',
                }}
              >
                {selectedOption === 'by-email' ? (
                  <Text>
                    We&apos;ve noticed that your custom template hasn&apos;t been updated to support
                    the inclusion of the remote payment link. You can still proceed with requesting
                    payment, and we&apos;ll use the default sales receipt instead. If you need
                    guidance on updating your print template, feel free to check out our{' '}
                    <TextLink target="_blank" href={HELP_CENTRE_LOCALIZED_LINK}>
                      Help Center
                    </TextLink>{' '}
                    for detailed instructions.
                  </Text>
                ) : (
                  <Text>
                    We&apos;ve observed that your custom template hasn&apos;t been updated with the
                    payment link. Please note that there won&apos;t be any mention of the payment
                    link or deposit request on your printed receipt until adjustments are made to
                    your custom print template. If you need guidance on updating your print
                    template, feel free to check out our{' '}
                    <TextLink target="_blank" href="">
                      Help Center
                    </TextLink>{' '}
                    for detailed instructions.
                  </Text>
                )}
              </Alert>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {isLoading ? (
          <Flex justifyContent="right">
            <Bone height={4} width={10} style={{ marginRight: '10px' }} />
            <Bone height={4} width={10} />
          </Flex>
        ) : (
          <Flex justifyContent="right">
            <Group>
              <Button
                onClick={() => setModalView('add-deposit')}
                data-test="request-paylink-back-btn"
              >
                Back
              </Button>
              <Button
                variant="secondary"
                fill
                onClick={() => {
                  if (selectedOption === 'paylink-only') {
                    handleSubmit();
                    setPreviousModalView('request-deposit-paylink');
                  } else {
                    setModalView('add-payment-deposit-email');
                  }
                }}
                data-test="request-paylink-next-btn"
              >
                {selectedOption === 'paylink-only' ? 'Done' : 'Next'}
              </Button>
            </Group>
          </Flex>
        )}
      </ModalFooter>
    </StyledRequestDepositModal>
  );
};

export default RequestDepositPaymentlink;
